import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

export default function TrialExpiredPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate  = useNavigate();
  const [checking, setChecking] = useState(false);
  const [notYet, setNotYet]     = useState(false);
  
  // Payment state
  const [paying, setPaying]   = useState(false);
  const [payMsg, setPayMsg]   = useState('');
  const [polling, setPolling] = useState(false);

  const trialEndDate = user?.shop?.trial_ends_at
    ? new Date(user.shop.trial_ends_at).toLocaleDateString("en-KE", {
        day: "numeric", month: "long", year: "numeric",
      })
    : null;

  const handleCheckStatus = async () => {
    setChecking(true);
    setNotYet(false);
    const updated = await refreshUser();
    const trialEndsAt = updated?.shop?.trial_ends_at;
    if (!trialEndsAt || new Date(trialEndsAt) > new Date()) {
      navigate("/dashboard");
    } else {
      setNotYet(true);
      setChecking(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handlePayNow = async (plan) => {
    setPaying(true);
    setPayMsg('');
    try {
      const res = await api.post('/payments/initiate', { plan });
      setPayMsg('✅ Check your phone and enter your M-Pesa PIN.');
      startPolling(res.data.checkout_request_id);
    } catch (err) {
      setPayMsg('❌ ' + (err.response?.data?.message || 'Failed to initiate payment'));
    } finally {
      setPaying(false);
    }
  };

  const startPolling = (cid) => {
    setPolling(true);
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await api.post('/payments/status', { checkout_request_id: cid });
        if (res.data.status === 'completed') {
          clearInterval(interval);
          setPolling(false);
          setPayMsg('🎉 Payment successful! Redirecting...');
          setTimeout(() => window.location.href = '/dashboard', 2000);
        } else if (res.data.status === 'failed') {
          clearInterval(interval);
          setPolling(false);
          setPayMsg('❌ Payment failed or cancelled. Please try again.');
        }
      } catch {}
      if (attempts >= 12) { // stop after 60 seconds
        clearInterval(interval);
        setPolling(false);
      }
    }, 5000);
  };

  const waMessage = encodeURIComponent(
    `Hi TumaTrack, I'd like to upgrade my plan for ${user?.shop?.name || "my business"}.`
  );

  return (
    <div
      className="min-h-screen bg-slate-900 flex items-center justify-center px-5 py-16 relative overflow-hidden"
      style={{ fontFamily: "'DM Sans', sans-serif" }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {/* Background glows */}
      <div className="absolute inset-0 opacity-10 pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 30% 50%, rgba(249,115,22,0.5) 0%, transparent 55%), radial-gradient(circle at 70% 20%, rgba(239,68,68,0.3) 0%, transparent 50%)" }}
      />
      <div className="absolute inset-0 opacity-5 pointer-events-none"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)", backgroundSize: "60px 60px" }}
      />

      <div className="relative w-full max-w-md">

        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/30">
              <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6 text-white" stroke="currentColor" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
              </svg>
            </div>
            <span className="text-2xl font-extrabold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>
              Tuma<span className="text-orange-400">Track</span>
            </span>
          </Link>
        </div>

        {/* Card */}
        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 shadow-2xl text-center">

          {/* Lock icon */}
          <div className="w-16 h-16 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </div>

          <h1 className="text-2xl font-extrabold text-white mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>
            Trial Expired
          </h1>

          <p className="text-slate-400 text-sm mb-1">
            The 7-day free trial for{" "}
            <span className="text-white font-semibold">{user?.shop?.name}</span>{" "}
            ended{trialEndDate ? ` on ${trialEndDate}` : ""}.
          </p>
          <p className="text-slate-500 text-xs mb-7">
            Upgrade your plan to restore access and keep your deliveries running.
          </p>

          {/* Plans */}
          <div className="space-y-2.5 mb-7 text-left">
            {[
              { plan: "Starter",    price: "KES 999/mo",   drivers: "Up to 3 drivers",   highlight: false },
              { plan: "Growth",     price: "KES 2,499/mo", drivers: "Up to 10 drivers",  highlight: true  },
              { plan: "Enterprise", price: "KES 5,999/mo", drivers: "Unlimited drivers", highlight: false },
            ].map((p) => (
              <div
                key={p.plan}
                className={`flex items-center justify-between px-4 py-3 rounded-xl border ${
                  p.highlight
                    ? "border-orange-500/40 bg-orange-500/10"
                    : "border-white/10 bg-white/5"
                }`}
              >
                <div>
                  <p className={`font-semibold text-sm ${p.highlight ? "text-orange-300" : "text-slate-200"}`}>
                    {p.plan} {p.highlight && <span className="text-xs bg-orange-500 text-white px-1.5 py-0.5 rounded-full ml-1">Popular</span>}
                  </p>
                  <p className="text-slate-500 text-xs">{p.drivers}</p>
                </div>
                <p className={`font-bold text-sm ${p.highlight ? "text-orange-400" : "text-slate-300"}`}>
                  {p.price}
                </p>
              </div>
            ))}
          </div>

          {/* Not upgraded yet message */}
          {notYet && (
            <div className="mb-4 bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs rounded-xl px-4 py-3">
              Your trial is still expired. Once you've paid, tap the button below to check again.
            </div>
          )}

          {/* CTAs */}
          <div className="space-y-3">
            <a
              href={`https://wa.me/254769177634?text=${waMessage}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2.5 w-full bg-orange-500 hover:bg-orange-400 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-orange-500/30 transition-all hover:-translate-y-0.5"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Upgrade via WhatsApp
            </a>

            {/* Payment section */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mt-5">
              <h3 className="font-bold text-gray-900 mb-1"
                style={{ fontFamily: "'Sora', sans-serif" }}>
                Activate Your Subscription
              </h3>
              <p className="text-gray-500 text-sm mb-5">
                Pay via M-Pesa to restore full access instantly
              </p>

              <div className="space-y-3">
                {[
                  { plan: 'starter',    label: 'Starter',    price: 'KES 999/mo',  riders: '3 riders' },
                  { plan: 'growth',     label: 'Growth',     price: 'KES 2,499/mo', riders: '10 riders' },
                  { plan: 'enterprise', label: 'Enterprise', price: 'KES 5,999/mo', riders: 'Unlimited' },
                ].map((p) => (
                  <div key={p.plan}
                    className="flex items-center justify-between p-4 border border-gray-100 rounded-xl">
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{p.label}</p>
                      <p className="text-gray-400 text-xs">{p.riders}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-gray-900 text-sm">{p.price}</span>
                      <button
                        onClick={() => handlePayNow(p.plan)}
                        disabled={paying || polling}
                        className="bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white font-semibold text-xs px-4 py-2 rounded-xl transition-colors"
                      >
                        {paying ? 'Sending...' : 'Pay Now'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {payMsg && (
                <div className={`mt-4 p-3 rounded-xl text-sm font-medium ${
                  payMsg.startsWith('🎉') || payMsg.startsWith('✅')
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                }`}>
                  {payMsg}
                  {polling && (
                    <span className="ml-2 inline-flex items-center gap-1 text-xs text-gray-400">
                      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10"
                          stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8v8H4z"/>
                      </svg>
                      Checking...
                    </span>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={handleCheckStatus}
              disabled={checking}
              className="w-full py-3 border border-white/10 hover:bg-white/5 text-slate-300 hover:text-white rounded-xl text-sm font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {checking ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Checking your status...
                </>
              ) : (
                "🔄 I've upgraded — restore my access"
              )}
            </button>

            <button
              onClick={handleLogout}
              className="w-full py-2.5 text-slate-600 hover:text-slate-400 text-sm transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>

        <p className="text-center text-slate-600 text-xs mt-5">
          Questions? WhatsApp{" "}
          <a href="https://wa.me/254700000001" className="text-slate-400 hover:text-white transition-colors">
            +254 700 000 001
          </a>
        </p>
      </div>
    </div>
  );
}